<template>
  <el-form
      @submit.prevent="validateSubmit('petForm')"
      ref="petForm"
      :model="pet"
      :rules="rules"
      label-position="top"
      novalidate
  >
    <input-section>
      <div class="flex flex-col md:flex-row justify-end">
        <div class="md:w-4/12 m-2">
          <horizontal-button :title="$t('actions.saveChanges')" isSuccess
                             faIcon="save"/>
        </div>

        <div class="md:w-4/12 m-2" v-if="enableClearForm">
          <horizontal-button
              :title="$t('actions.clearButton')"
              isDanger
              faIcon="trash"
              @click.prevent="clearForm('petForm')"
          />
        </div>
      </div>
    </input-section>

    <!-- @submit errors -->
    <alert-badge
        isDanger
        :title="error.title"
        :body="error.body"
        @dismissed="
        () => {
          error.title = ''
          error.body = ''
        }
      "
    />

    <input-section>
      <h1>{{ t('sections.petInfo') }}</h1>

      <div class="grid grid-cols-2 gap-2">
        <el-form-item :label="t('petName')" prop="name" class="input-label">
          <el-input
              id="name"
              v-model="pet.name"
              :placeholder="t('placeholders.petName')"
              maxlength="127"
              show-word-limit
              clearable
              required
          >
          </el-input>
        </el-form-item>

        <el-form-item :label="t('type')" prop="type" class="input-label">
          <el-select v-model="pet.type" filterable clearable class="w-full">
            <el-option
              v-for="petType in petTypes"
              :key="petType"
              :label="petType"
              :value="petType"
            />
          </el-select>
        </el-form-item>
      </div>

      <div class="grid grid-cols-4 gap-2">
        <el-form-item :label="t('breed')" prop="breed" class="input-label">
          <deletable-autocomplete
              id="breed"
              v-model="pet.breed"
              :placeholder="t('placeholders.breed')"
              :fetch-suggestions="breedSearch"
              class="w-full"
              :maxlength="127"
          />
        </el-form-item>

        <el-form-item :label="t('color')" prop="color" class="input-label">
          <deletable-autocomplete
              id="color"
              v-model="pet.color"
              :placeholder="t('placeholders.color')"
              :fetch-suggestions="colorSearch"
              class="w-full"
              :maxlength="127"
          />
        </el-form-item>

        <el-form-item :label="t('sex')" prop="sex" class="input-label">
          <el-select v-model="pet.sex" filterable clearable class="w-full">
            <el-option :label="t('male')" value="male"></el-option>
            <el-option :label="t('female')" value="female"></el-option>
          </el-select>
        </el-form-item>

        <el-form-item :label="t('neutered')" prop="neutered" class="input-label">
          <el-checkbox v-model="pet.neutered" />
        </el-form-item>
      </div>

      <el-form-item :label="t('dateOfBirth')" prop="dateOfBirth" class="input-label">
        <el-date-picker
            id="dateOfBirth"
            v-model="pet.dateOfBirth"
            type="date"
            format="DD-MM-YYYY"
            placeholder="Pick a date"
            style="width: 100%"
        />
      </el-form-item>

      <el-form-item :label="t('ownerName')" prop="customerID" class="input-label">
        <el-select
            v-model="selectedCustomer"
            filterable
            clearable
            remote
            :remote-method="searchCustomers"
            :placeholder="t('selectOwner')"
            value-key="id"
            class="w-full"
            @change="onCustomerChange"
        >
          <el-option
              v-for="customer in customers"
              :key="customer.id"
              :label="customer.contactDisplayName"
              :value="customer"
          >
          </el-option>
        </el-select>
      </el-form-item>
    </input-section>
  </el-form>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue'
import { useI18n } from 'vue-i18n'
import Pet, { petI18nMessages } from '@/models/Pet'
import Customer from '@/models/customer/Customer'
import HorizontalButton from '@/components/HorizontalButton.vue'
import AlertBadge from '@/components/AlertBadge.vue'
import InputSection from '@/components/form/InputSection.vue'
import DeletableAutocomplete from '@/components/form/DeletableAutocomplete.vue'
import { useSettingsStore } from '@/stores/settingsStore'

export default defineComponent({
  name: 'pet-form',

  setup() {
    const { t } = useI18n({
      messages: {
        en: {
          ...petI18nMessages.en,
          sections: {
            petInfo: 'Pet Information'
          },
          placeholders: {
            petName: 'Enter pet name',
            breed: 'Enter breed',
            color: 'Enter color'
          }
        },
        ar: {
          ...petI18nMessages.ar,
          sections: {
            petInfo: 'معلومات الأليف'
          },
          placeholders: {
            petName: 'أدخل اسم الأليف',
            breed: 'أدخل السلالة',
            color: 'أدخل اللون'
          }
        }
      }
    })

    return { t }
  },

  components: {
    HorizontalButton,
    AlertBadge,
    InputSection,
    DeletableAutocomplete
  },

  props: {
    initPet: {
      type: Object as PropType<Pet>,
      required: true
    },
    successMsg: {
      type: String,
      required: true
    },
    submitAction: {
      type: Function,
      required: true
    },
    enableClearForm: {
      type: Boolean,
      default: false
    },
    clearFormOnSubmit: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      pet: Pet.from(this.initPet),
      customers: [] as Customer[],
      selectedCustomer: null as Customer | null,
      petTypes: [] as string[],
      error: {
        title: '',
        body: ''
      },
      rules: {
        name: [
          { required: true, message: this.t('validation.required'), trigger: 'blur' }
        ],
        type: [
          { required: true, message: this.t('validation.required'), trigger: 'change' }
        ],
        customerID: [
          { required: true, message: this.t('validation.required'), trigger: 'change' }
        ]
      }
    }
  },

  async mounted() {
    // Load pet types from settings
    const settingsStore = useSettingsStore()
    const petTypesValue = settingsStore.getValue('pet_types')
    if (petTypesValue) {
      try {
        this.petTypes = typeof petTypesValue === 'string'
          ? JSON.parse(petTypesValue)
          : petTypesValue
      } catch (e) {
        console.error('Error parsing pet_types:', e)
        this.petTypes = []
      }
    }

    // Initial search to populate dropdown
    // For new pets, load initial list
    // For existing pets, the watcher will have already set the selected customer
    await this.searchCustomers('')
  },

  methods: {
    async searchCustomers(query: string) {
      if (!query) {
        query = ''
      }
      try {
        const url = `${Customer.ENDPOINT}?q=${query}`
        const customersRes = await this.$http.get<Customer[]>(url)
        let customersList = customersRes.map((customer) => Customer.from(customer))

        // Ensure selected customer is in the list if it exists
        if (this.selectedCustomer && !customersList.some(c => c.id === this.selectedCustomer?.id)) {
          customersList = [this.selectedCustomer, ...customersList]
        }

        this.customers = customersList
      } catch (error: any) {
        this.$alertModal.showDanger({ title: error.title })
        // Keep selected customer in list even on error
        if (this.selectedCustomer) {
          this.customers = [this.selectedCustomer]
        } else {
          this.customers = []
        }
      }
    },

    onCustomerChange() {
      if (this.selectedCustomer) {
        this.pet.customerID = this.selectedCustomer.id || ''
        this.pet.customer = this.selectedCustomer
      } else {
        this.pet.customerID = ''
        this.pet.customer = null
      }
    },

    async validateSubmit(formName: string) {
      const form = this.$refs[formName] as any

      form.validate(async (valid: boolean) => {
        if (valid) {
          try {
            const error = await this.submitAction(this.pet)

            if (error) {
              this.error.title = error.title
              this.error.body = error.body
            } else {
              this.$alertModal.showSuccess({ title: this.successMsg })

              if (this.clearFormOnSubmit) {
                this.clearForm(formName)
              }
            }
          } catch (err: any) {
            this.error.title = err.title || 'Error'
            this.error.body = err.body || 'An error occurred'
          }
        } else {
          this.error.title = this.$t('validation.inputErrors')
          this.error.body = this.$t('validation.required')
        }
      })
    },

    clearForm(formName: string) {
      const form = this.$refs[formName] as any
      form.resetFields()
      this.pet = Pet.from(new Pet())
      this.error.title = ''
      this.error.body = ''
    },

    async breedSearch(queryString: string, cb: (suggestions: { value: string; id?: number }[]) => void) {
      try {
        const response = await this.$http.get<{ id?: number; value: string }[]>(
          `/helpers/suggestions?model=customer_pets&key=breed&query=${queryString}`
        )
        const result = response.map((r) => ({
          value: r.value,
          id: r.id
        }))
        cb(result)
      } catch (e) {
        console.log(e)
        cb([])
      }
    },

    async colorSearch(queryString: string, cb: (suggestions: { value: string; id?: number }[]) => void) {
      try {
        const response = await this.$http.get<{ id?: number; value: string }[]>(
          `/helpers/suggestions?model=customer_pets&key=color&query=${queryString}`
        )
        const result = response.map((r) => ({
          value: r.value,
          id: r.id
        }))
        cb(result)
      } catch (e) {
        console.log(e)
        cb([])
      }
    }
  },

  watch: {
    initPet: {
      handler(newVal) {
        this.pet = Pet.from(newVal)
        // Update selected customer when pet data changes
        if (this.pet.customer && this.pet.customerID) {
          const customer = Customer.from(this.pet.customer)
          this.selectedCustomer = customer
          // Ensure the customer is in the dropdown list
          this.customers = [customer]
        } else {
          this.selectedCustomer = null
          this.customers = []
        }
      },
      deep: true,
      immediate: true
    }
  }
})
</script>

<style scoped>
</style>
